import type { Purchase } from "./schemas/purchase";
import { vendorDeliveryFeeMinor } from "./schemas/vendor-checkout";
import { localTaxWords } from "./store-money";

/** What an order comes to (§2.3). `complete` of 0 is the payment policy's to
 *  judge (`freeOrders`), never the total's. */
export type Total =
  | { kind: "empty" }
  | { kind: "not_for_sale" }
  /** A store's live delivery option is not chosen yet. */
  | { kind: "pending_delivery"; subtotalMinor: number }
  /** `discountMinor`: what a club discount code takes off (the members'
   *  prices of the chosen items, LIS-1178); 0 without one. */
  | {
      kind: "complete";
      totalMinor: number;
      deliveryMinor: number | null;
      discountMinor: number;
      /** The tax its seller adds at checkout, where the purchase carries
       *  its rate (`taxRateBps`, LIS-1194): each line's and the order's
       *  fees and delivery's, rounded each, halves to even, as Shopify
       *  rounds them. Null: not known ("+ local tax"). */
      taxMinor: number | null;
    }
  | { kind: "invalid"; reason: "quantity_out_of_range" | "unsafe_integer" };

const invalidQuantity: Total = { kind: "invalid", reason: "quantity_out_of_range" };
const unsafe: Total = { kind: "invalid", reason: "unsafe_integer" };

/**
 * The one total both clients and the server run on a purchase: the lines'
 * prices and booking fees, the order fee (flat, plus the basket ladder of
 * tickets) and the delivery of the goods, less a member's club discount
 * code (each chosen item's `memberPriceMinor` below its price). Fees and
 * delivery are the listing's at its own prices, as the seller's basket
 * reads them before the code. `delivery` is the chosen quoted option, or
 * null.
 */
export function orderTotal(
  purchase: Pick<Purchase, "picker" | "maxOrderQuantity" | "fees" | "delivery" | "sale"> & Partial<Pick<Purchase, "taxRateBps" | "regionTaxRatesBps">>,
  lines: readonly { id: string; quantity: number }[],
  delivery: { feeMinor: number } | null,
  /** The buyer's state or province, as their address answers it ("TX",
   *  "US-TX"): its rate where the purchase has one (`regionTaxRatesBps`). */
  region: string | null = null,
): Total {
  if (purchase.sale.kind === "demo") return { kind: "not_for_sale" };
  const chosen = lines.filter((line) => line.quantity !== 0);
  if (!chosen.length) return { kind: "empty" };

  const seen = new Set<string>();
  let units = 0;
  let subtotalMinor = 0;
  let discountMinor = 0;
  const lineTotals: number[] = [];
  for (const line of chosen) {
    if (seen.has(line.id)) return invalidQuantity;
    seen.add(line.id);
    const item = purchase.picker.items.find((candidate) => candidate.id === line.id);
    if (
      !item ||
      !item.available ||
      !Number.isInteger(line.quantity) ||
      line.quantity < 1 ||
      line.quantity < item.min ||
      line.quantity > (item.max ?? Infinity)
    )
      return invalidQuantity;
    units += line.quantity;
    subtotalMinor += line.quantity * (item.priceMinor + item.feeMinor);
    const lineDiscount = item.memberPriceMinor !== undefined ? line.quantity * (item.priceMinor - item.memberPriceMinor) : 0;
    discountMinor += lineDiscount;
    lineTotals.push(line.quantity * (item.priceMinor + item.feeMinor) - lineDiscount);
  }
  if (purchase.maxOrderQuantity !== null && units > purchase.maxOrderQuantity) return invalidQuantity;
  if (!Number.isSafeInteger(subtotalMinor) || !Number.isSafeInteger(discountMinor)) return unsafe;

  const kind = purchase.delivery.kind;
  if (kind === "unmeasured") return { kind: "not_for_sale" };
  if (kind === "quoted" && delivery === null) return { kind: "pending_delivery", subtotalMinor };

  const orderFeeMinor =
    subtotalMinor <= 0
      ? 0
      : purchase.fees.orderFeeMinor + vendorDeliveryFeeMinor(purchase.fees.ladder, subtotalMinor);
  const deliveryMinor =
    purchase.delivery.kind === "ladder"
      ? vendorDeliveryFeeMinor(purchase.delivery.tiers, subtotalMinor)
      : purchase.delivery.kind === "quoted"
        ? (delivery?.feeMinor ?? 0)
        : // Tickets and a download the store emails: no delivery at all.
          null;
  const totalMinor = subtotalMinor + orderFeeMinor + (deliveryMinor ?? 0) - discountMinor;
  if (!Number.isSafeInteger(totalMinor)) return unsafe;
  const rate = taxRateFor(purchase, region);
  const taxMinor = rate
    ? [...lineTotals, orderFeeMinor + (deliveryMinor ?? 0)].reduce((sum, charge) => sum + taxOn(charge, rate), 0)
    : null;
  return { kind: "complete", totalMinor, deliveryMinor, discountMinor, taxMinor };
}

/** The state or province the buyer's address answers name, for its tax
 *  (`orderTotal`'s `region`), or null. */
export function answeredRegion(answers: readonly { answer: unknown }[]): string | null {
  for (const { answer } of answers)
    if (answer && typeof answer === "object" && "line1" in answer && "region" in answer) {
      const region = (answer as { region?: string | null }).region?.trim();
      return region || null;
    }
  return null;
}

/** The tax rate the checkout says for the buyer: the purchase's, or its
 *  rate for the buyer's state or province; undefined: "+ local tax". */
export function taxRateFor(purchase: Partial<Pick<Purchase, "taxRateBps" | "regionTaxRatesBps">>, region: string | null): number | undefined {
  return purchase.taxRateBps ?? regionRateOf(purchase, region);
}

/** The rate of the buyer's state or province, by its code, the country
 *  prefix an address may carry ("US-TX") left off. */
function regionRateOf(purchase: Partial<Pick<Purchase, "regionTaxRatesBps">>, region: string | null): number | undefined {
  if (!region || !purchase.regionTaxRatesBps) return undefined;
  return purchase.regionTaxRatesBps[region.toUpperCase().replace(/^[A-Z]{2}-/, "")];
}

/** `rateBps` of `minor`, halves to even. */
function taxOn(minor: number, rateBps: number): number {
  const scaled = minor * rateBps;
  const whole = Math.floor(scaled / 10_000);
  const rest = scaled - whole * 10_000;
  return rest > 5_000 || (rest === 5_000 && whole % 2 === 1) ? whole + 1 : whole;
}

/** What a purchase's total says after it: null where its price includes
 *  tax; where its seller adds tax at checkout, the tax the total holds
 *  (`taxMinor`, LIS-1194: "+ £0.20 local tax"), or "+ local tax" where it is
 *  not known (LIS-1133). */
export const purchaseTaxWords = (purchase: Pick<Purchase, "tax" | "currency">, total: Total | null): string | null =>
  purchase.tax === "added_at_checkout"
    ? localTaxWords(total?.kind === "complete" ? total.taxMinor : null, purchase.currency)
    : null;
